/**
 * Pure helpers for building and upserting booking form answers.
 *
 * Extracted from `updateAnswer` in `../user-form-step.tsx` so the
 * questionId/name stamping — the whole point of this plan (protecting a
 * production report column from forking when a question is renamed) — has a
 * regression net without pulling RTL/jsdom into packages/ui (vitest-only
 * here). Same extract-and-unit-test pattern already used for page
 * navigation in `./question-pages.ts`.
 */

export type AnswerQuestion = {
  _id: string;
  title: string;
  /** Stable question identifier, stamped onto the answer alongside `_id`. */
  name?: string;
  systemRole?: string | null;
};

export type AnswerValue = string | boolean | Date | null | number | string[];

export type AnswerEntry = {
  index: number;
  guestIndex: number | null;
  label: string;
  value: AnswerValue;
  systemRole?: string | null;
  /** Stable question _id. Absent on answers recorded before Aug 2026. */
  questionId?: string;
  /** Stable question identifier (`question.name`). Absent on historic answers. */
  name?: string;
};

/**
 * The label stored on an answer: per-guest questions prefix with the guest
 * number (matching the rendered "Guest N" heading), non-per-guest questions
 * use the bare question title. `guestIndex` is 0-based; the displayed guest
 * number is 1-based.
 */
export function answerLabelFor(
  question: { title: string },
  guestIndex: number | null,
): string {
  return guestIndex === null
    ? question.title
    : `Guest ${guestIndex + 1} - ${question.title}`;
}

export type ApplyAnswerUpdateArgs = {
  questionIndex: number;
  guestIndex: number | null;
  label: string;
  value: AnswerValue | undefined;
  question: AnswerQuestion;
};

/**
 * Upserts (or removes, on a blank value) the answer for
 * `{questionIndex, guestIndex}` and returns the NEW array — `values` is
 * never mutated. Stamps `questionId`/`name` alongside the existing `label`,
 * so a later question rename doesn't fork the report column this answer
 * feeds (readers fall back to `label` for historic rows that predate this
 * stamp).
 */
export function applyAnswerUpdate(
  values: AnswerEntry[],
  { questionIndex, guestIndex, label, value, question }: ApplyAnswerUpdateArgs,
): AnswerEntry[] {
  const existingIndex = values.findIndex(
    (item) => item.index === questionIndex && item.guestIndex === guestIndex,
  );

  if (value === null || value === "" || value === undefined) {
    return values.filter(
      (item) => !(item.index === questionIndex && item.guestIndex === guestIndex),
    );
  }

  const entry: AnswerEntry = {
    index: questionIndex,
    guestIndex,
    label,
    value,
    // Every server-persisted question carrying a `systemRole` also has
    // `isPerGuest === true` (enforced by `sanitizeSystemQuestionRoles` on
    // save), so in practice the non-per-guest call site always derives
    // `null` here. That invariant lives in the save path, not the DB — it
    // isn't schema-enforced, so don't assume it holds without re-checking
    // `sanitizeSystemQuestionRoles` if this ever changes.
    systemRole: question.systemRole ?? null,
    questionId: question._id,
    name: question.name,
  };

  if (existingIndex !== -1) {
    const newValues = [...values];
    newValues[existingIndex] = entry;
    return newValues;
  }

  return [...values, entry];
}

/**
 * Drops per-guest answers for guests that no longer exist.
 *
 * `UserFormStep` renders and validates per-guest inputs across
 * `guestIndex < totalGuests`, but it never REMOVES an answer when that count
 * falls — and it deliberately stops accepting parent updates once the buyer has
 * typed (`hasLocalChangesRef`), so a parent cannot fix this by filtering the
 * `values` prop. The only parent-side lever is a full reset, which would throw
 * away answers the buyer still needs.
 *
 * So prune at the payload boundary instead. The stale answer stays in the
 * component's internal state where it is invisible (nothing renders it) and
 * comes back if the count rises again — which is what a buyer nudging a
 * quantity stepper up and down would expect.
 *
 * On the rental flow the count can also fall on its OWN: the pool-availability
 * response clamps `units` down asynchronously, so a hire configured for three
 * can become a hire for two while the buyer is mid-form.
 *
 * Non-per-guest answers carry `guestIndex === null` and are never pruned.
 *
 * Generic on the element type: `AnswerEntry` here and `UserFormAnswer` in
 * `../user-form-step` are the same shape declared twice, and constraining to
 * the one field this reads keeps whichever the caller holds.
 */
export function pruneAnswersToGuestCount<
  T extends { guestIndex: number | null },
>(values: T[], totalGuests: number): T[] {
  return values.filter(
    (answer) => answer.guestIndex === null || answer.guestIndex < totalGuests,
  );
}

/**
 * Drops answers whose QUESTION no longer renders.
 *
 * The sibling above closes "this guest no longer exists"; this closes "this
 * question is no longer being asked", which is a different hole and not
 * reachable through a guest count. A question stops rendering when the flow
 * changes one of the flags isQuestionRenderable consults - on the rental flow,
 * switching to a ticket that occupies no seat suppresses the system identity
 * questions, and a school on the attendees feature suppresses them outright.
 * Without this, a buyer who answered "Guest 2 - First name" before that switch
 * still ships it, recording identity against guests the server never creates.
 *
 * renderedQuestionIndices are indices into the SORTED question array - the
 * same basis buildQuestionPages emits and applyAnswerUpdate stamps, so the two
 * line up as long as both sort by `order`.
 *
 * Like the sibling, this belongs at the payload boundary: the answer stays in
 * the form's own state, so re-selecting the original ticket brings it back
 * rather than making the buyer type it twice.
 */
export function pruneAnswersToRenderedQuestions<T extends { index: number }>(
  values: T[],
  renderedQuestionIndices: Iterable<number>,
): T[] {
  const rendered = new Set(renderedQuestionIndices);
  return values.filter((answer) => rendered.has(answer.index));
}
